import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import './SharesPage.css';

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function StatusBadge({ share, t }) {
  if (share.replied) return <span className="shares-badge replied">{t('shares.status.replied')}</span>;
  if (share.open_count > 0) return <span className="shares-badge opened">{t('shares.status.opened')}</span>;
  return <span className="shares-badge sent">{t('shares.status.sent')}</span>;
}

function ShareRow({ share, onToggleReplied, onDelete, t }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}${share.path}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked; the user can open the link column manually.
    }
  };

  return (
    <tr>
      <td>{share.recipient || <span style={{ color: '#98a2b3' }}>—</span>}</td>
      <td className="shares-num">{share.findings_count}</td>
      <td className="shares-num">{share.open_count}</td>
      <td>{fmtDate(share.last_opened_at)}</td>
      <td><StatusBadge share={share} t={t} /></td>
      <td>
        <button type="button" className="shares-linkbtn" onClick={copy} title={url}>
          {copied ? <Check size={14} /> : <Copy size={14} />}
          {copied ? t('shares.copied') : t('shares.copy')}
        </button>
      </td>
      <td>
        <button
          type="button"
          className={`shares-replybtn${share.replied ? ' on' : ''}`}
          onClick={() => onToggleReplied(share)}
        >
          {share.replied ? <Check size={14} /> : null}
          {share.replied ? t('shares.markedReplied') : t('shares.markReplied')}
        </button>
      </td>
      <td>
        <button
          type="button"
          className="shares-delbtn"
          onClick={() => onDelete(share)}
          title={t('shares.delete')}
          aria-label={t('shares.delete')}
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

export default function SharesPage() {
  const { t } = useTranslation('common');
  const [shares, setShares] = useState(null);
  const [error, setError] = useState('');
  const [onlyFollowUp, setOnlyFollowUp] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .listQaShares()
      .then((data) => alive && setShares(data))
      .catch((e) => alive && setError(e.response?.data?.detail || e.message));
    return () => {
      alive = false;
    };
  }, []);

  const toggleReplied = async (share) => {
    const updated = await api.setQaShareReplied(share.token, !share.replied);
    setShares((prev) => prev.map((s) => (s.token === share.token ? updated : s)));
  };

  const deleteShare = async (share) => {
    if (!window.confirm(t('shares.confirmDelete'))) return;
    await api.deleteQaShare(share.token);
    setShares((prev) => prev.filter((s) => s.token !== share.token));
  };

  const visible = useMemo(() => {
    if (!shares) return [];
    return onlyFollowUp
      ? shares.filter((s) => s.open_count > 0 && !s.replied)
      : shares;
  }, [shares, onlyFollowUp]);

  if (error) return <div className="shares-state">{error}</div>;
  if (!shares) return <div className="shares-state">{t('shares.loading')}</div>;

  return (
    <div className="shares-page">
      <div className="shares-head">
        <h1 className="shares-title">{t('shares.title')}</h1>
        <p className="shares-sub">{t('shares.subtitle')}</p>
      </div>

      {shares.length === 0 ? (
        <div className="shares-empty">{t('shares.empty')}</div>
      ) : (
        <>
          <div className="shares-toolbar">
            <label className="shares-filter">
              <input
                type="checkbox"
                checked={onlyFollowUp}
                onChange={(e) => setOnlyFollowUp(e.target.checked)}
              />
              {t('shares.needsFollowUp')}
            </label>
          </div>

          <div className="shares-table-wrap">
            <table className="shares-table">
              <thead>
                <tr>
                  <th>{t('shares.col.recipient')}</th>
                  <th>{t('shares.col.findings')}</th>
                  <th>{t('shares.col.opens')}</th>
                  <th>{t('shares.col.lastOpened')}</th>
                  <th>{t('shares.col.status')}</th>
                  <th>{t('shares.col.link')}</th>
                  <th>{t('shares.col.reply')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((s) => (
                  <ShareRow
                    key={s.token}
                    share={s}
                    onToggleReplied={toggleReplied}
                    onDelete={deleteShare}
                    t={t}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {visible.length === 0 && (
            <div className="shares-empty">{t('shares.noFollowUp')}</div>
          )}
        </>
      )}
    </div>
  );
}
